import { ArrowLeft } from 'lucide-react'

import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { DisclosureConfigForm } from '@/features/loans/disclosure-config-form'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths } from '@/features/loans/loan-paths'
import { LOAN_PERMISSIONS, useLoanPermissions } from '@/features/loans/permissions'

/**
 * Choosing which rate headlines the pre-agreement disclosure (questionnaire item 9).
 *
 * Configuration, countersigned, and consequential in the quiet way: it decides which figure a
 * member reads first on a regulated document. It refuses a narrow display for the same reason the
 * classification scheme screen does — rarely urgent, permanently consequential.
 */
export function LoanDisclosureConfigPage() {
  const permissions = useLoanPermissions()

  return (
    <RequiresWiderScreen
      screen="The disclosure headline"
      reason="It decides which rate a member reads first on the key facts statement."
    >
      <LoanLink
        to={loanPaths.products}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-text hover:text-brand-text-hover"
      >
        <ArrowLeft className="size-3.5" aria-hidden />
        Back to products
      </LoanLink>

      <PageHeader
        title="Disclosure headline"
        description="Which of the three rates — quoted flat, nominal annual, or effective annual — leads the key facts statement. All three always print."
      />

      {permissions.canConfigureDisclosure ? (
        <DisclosureConfigForm />
      ) : (
        <Alert tone="warning">
          <AlertTitle>Your role cannot change the disclosure headline</AlertTitle>
          <AlertDescription>
            This needs{' '}
            <span className="font-mono text-xs">{LOAN_PERMISSIONS.disclosureConfig}</span>, and a
            second person holding{' '}
            <span className="font-mono text-xs">{LOAN_PERMISSIONS.disclosureConfigApprove}</span>{' '}
            to countersign it.
          </AlertDescription>
        </Alert>
      )}
    </RequiresWiderScreen>
  )
}
